/**
 * Extra printplaatsen bij Printful.
 *
 * Printful rekent de variantprijs inclusief een print op de voorkant. Elke
 * extra plaats (rug, mouwen, label, ...) kost een vaste toeslag per stuk
 * (`result.product.files[].additional_price` in de publieke catalogus). Die
 * toeslag hoort bij de inkoopprijs, dus vóór de 2,2-opslag, staffelkorting en
 * margebewaking: zo krijgt hij dezelfde opslag als de rest van het product.
 *
 * Bron van de bedragen: `Product.specifications.printfulPlacementPrices`,
 * gevuld door scripts/sync-printful-placement-prices.ts en door de
 * admin-catalogussync. Ontbreekt een bedrag, dan valt de prijs terug op een
 * ruime standaardtoeslag, zodat we nooit te weinig rekenen.
 */

/** Plaatsen die in de variantprijs zitten. */
const INCLUDED_PLACEMENTS = new Set(['default', 'front']);

/** Conservatieve terugval wanneer de opgeslagen toeslag ontbreekt. */
export const PRINTFUL_DEFAULT_PLACEMENT_SURCHARGE = 5.95;

export const PRINTFUL_PLACEMENT_PRICES_KEY = 'printfulPlacementPrices';

export function normalizePrintfulPlacement(value: unknown) {
  return String(value ?? '').trim().toLowerCase();
}

export function isIncludedPrintfulPlacement(placement: string) {
  return INCLUDED_PLACEMENTS.has(normalizePrintfulPlacement(placement) || 'default');
}

function nonEmptyString(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0;
}

function addPlacement(target: Set<string>, value: unknown) {
  const placement = normalizePrintfulPlacement(value);
  if (placement) target.add(placement);
}

/**
 * Systeemlagen van de editor (mockup, printvlak, hulplijnen) tellen niet mee.
 * Zelfde regel als isEditorSystemObject in design-editor-modal.tsx.
 */
function isUserCanvasObject(object: any) {
  if (!object || typeof object !== 'object') return false;
  if (object.templateType || object.edgeType === 'canvas-edge' || object.guideType === 'print-guide') return false;
  if ((object.stroke === 'red' || object.stroke === 'green') && object.selectable === false && object.evented === false) return false;
  return true;
}

/**
 * Welke Printful-plaatsen bevatten ontwerp? Een plaats telt als er een
 * niet-leeg bestand voor is (geüploade `imageUrl` of nog niet geüploade
 * `dataURL`), of als het canvas een gebruikerslaag op die plaats heeft.
 * Zelfde bron als printfulFilesFromDesignData, wat naar Printful gaat.
 */
export function usedPrintfulPlacements(designData: unknown): string[] {
  const data = designData && typeof designData === 'object' ? designData as Record<string, any> : null;
  if (!data) return [];
  const placements = new Set<string>();

  const files = Array.isArray(data.printful?.files) ? data.printful.files : [];
  for (const file of files) {
    if (!file || typeof file !== 'object') continue;
    if (nonEmptyString(file.imageUrl) || nonEmptyString(file.dataURL)) addPlacement(placements, file.placement);
  }

  const objects = Array.isArray(data.json?.objects) ? data.json.objects : [];
  for (const object of objects) {
    if (isUserCanvasObject(object) && nonEmptyString(object.printfulPlacement)) {
      addPlacement(placements, object.printfulPlacement);
    }
  }

  return Array.from(placements).sort();
}

/** Plaatsen van alle bestanden van een regel samen (winkelwagen/kassa). */
export function usedPrintfulPlacementsFromFiles(files: Array<{ designData?: unknown } | null | undefined>): string[] {
  const placements = new Set<string>();
  for (const file of files) {
    for (const placement of usedPrintfulPlacements(file?.designData)) placements.add(placement);
  }
  return Array.from(placements).sort();
}

/** Een door de browser meegestuurde lijst opschonen (alleen voor weergave). */
export function sanitizePrintfulPlacements(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const placements = new Set<string>();
  for (const item of value.slice(0, 20)) {
    if (typeof item !== 'string') continue;
    const placement = normalizePrintfulPlacement(item);
    if (placement && placement.length <= 64 && /^[a-z0-9_-]+$/.test(placement)) placements.add(placement);
  }
  return Array.from(placements).sort();
}

function storedPlacementPrices(specifications: unknown): Record<string, unknown> | null {
  const specs = specifications && typeof specifications === 'object' ? specifications as Record<string, any> : null;
  const prices = specs?.[PRINTFUL_PLACEMENT_PRICES_KEY];
  return prices && typeof prices === 'object' && !Array.isArray(prices) ? prices as Record<string, unknown> : null;
}

/**
 * Inkooptoeslag per stuk voor één plaats. Een opgeslagen null/0 is gratis;
 * een plaats zonder opgeslagen bedrag krijgt de conservatieve terugval.
 */
export function printfulPlacementSurchargeCost(placement: string, specifications: unknown) {
  const key = normalizePrintfulPlacement(placement) || 'default';
  if (isIncludedPrintfulPlacement(key)) return 0;
  const prices = storedPlacementPrices(specifications);
  if (prices && Object.prototype.hasOwnProperty.call(prices, key)) {
    const stored = prices[key];
    if (stored === null || stored === undefined || stored === '') return 0;
    const amount = Number(stored);
    if (Number.isFinite(amount) && amount >= 0) return Math.round(amount * 100) / 100;
  }
  return fallbackPlacementSurcharge(key);
}

/**
 * Terugval zonder opgeslagen bedrag: het hoogste bedrag dat de catalogus voor
 * zo'n plaats vraagt (droge run 11 okt 2026, 258 producten). Plaatsen die in
 * de catalogus nooit iets kosten (kalendermaanden, all-over-panelen, zak,
 * `embroidery_front`, `top_back`, ...) blijven gratis; anders zou een
 * kalender met twaalf maanden twaalf keer de terugval krijgen.
 */
function fallbackPlacementSurcharge(key: string) {
  if (key === 'embroidery_front' || key === 'addon_embroidery_front' || key === 'embroidery_patch_front') return 0;
  if (key === 'embroidery_apparel_front') return 6.95;
  if (key === 'embroidery_front_large') return 3.95;
  if (/^embroidery_/.test(key)) return 2.95;
  if (/^label_(inside|outside)(_dtf)?$/.test(key)) return 2.49;
  if (/^back(_|$)/.test(key)) return PRINTFUL_DEFAULT_PLACEMENT_SURCHARGE;
  if (/sleeve/.test(key)) return PRINTFUL_DEFAULT_PLACEMENT_SURCHARGE;
  if (/(^|_)dtf(_|$)/.test(key) || key === 'front_large') return PRINTFUL_DEFAULT_PLACEMENT_SURCHARGE;
  return 0;
}

export type PrintfulPlacementSurcharge = {
  placement: string;
  /** Inkooptoeslag per stuk (intern). */
  cost: number;
};

/** Toeslagen per gebruikte, niet-inbegrepen plaats. */
export function printfulPlacementSurcharges(placements: string[], specifications: unknown): PrintfulPlacementSurcharge[] {
  const seen = new Set<string>();
  const result: PrintfulPlacementSurcharge[] = [];
  for (const raw of placements) {
    const placement = normalizePrintfulPlacement(raw);
    if (!placement || seen.has(placement) || isIncludedPrintfulPlacement(placement)) continue;
    seen.add(placement);
    const cost = printfulPlacementSurchargeCost(placement, specifications);
    if (cost > 0) result.push({ placement, cost });
  }
  return result;
}

/** Totale inkooptoeslag per stuk. */
export function printfulPlacementExtraUnitCost(placements: string[], specifications: unknown) {
  const total = printfulPlacementSurcharges(placements, specifications)
    .reduce((sum, item) => sum + item.cost, 0);
  return Math.round(total * 100) / 100;
}

type PlacementLocale = 'nl' | 'en' | 'de' | 'fr';

const PLACEMENT_LABELS: Record<string, Record<PlacementLocale, string>> = {
  back: { nl: 'Rugprint', en: 'Back print', de: 'Rückendruck', fr: 'Impression au dos' },
  sleeve_left: { nl: 'Print linkermouw', en: 'Left sleeve print', de: 'Druck linker Ärmel', fr: 'Impression manche gauche' },
  sleeve_right: { nl: 'Print rechtermouw', en: 'Right sleeve print', de: 'Druck rechter Ärmel', fr: 'Impression manche droite' },
  label_outside: { nl: 'Print label buitenkant', en: 'Outside label print', de: 'Druck Etikett außen', fr: 'Impression étiquette extérieure' },
  label_inside: { nl: 'Print label binnenkant', en: 'Inside label print', de: 'Druck Etikett innen', fr: 'Impression étiquette intérieure' },
};

const GENERIC_PLACEMENT_LABEL: Record<PlacementLocale, string> = {
  nl: 'Extra printplaats',
  en: 'Extra print area',
  de: 'Zusätzlicher Druckbereich',
  fr: 'Zone d’impression supplémentaire',
};

/** Klantlabel voor een toeslagregel. Geen leveranciersnamen. */
export function printfulPlacementLabel(placement: string, locale: string) {
  const lang: PlacementLocale = locale === 'en' || locale === 'de' || locale === 'fr' ? locale : 'nl';
  const key = normalizePrintfulPlacement(placement);
  const base = key.replace(/_(dtf|large|print)$/, '');
  if (PLACEMENT_LABELS[key]) return PLACEMENT_LABELS[key][lang];
  if (PLACEMENT_LABELS[base]) return PLACEMENT_LABELS[base][lang];
  if (/^back/.test(key)) return PLACEMENT_LABELS.back[lang];
  return GENERIC_PLACEMENT_LABEL[lang];
}

/**
 * Bedragen uit de publieke catalogus (`result.product.files`) omzetten naar
 * het opgeslagen formaat: { back: 5.95, sleeve_left: 2.49, front: null }.
 */
export function placementPricesFromCatalogFiles(files: unknown): Record<string, number | null> {
  const prices: Record<string, number | null> = {};
  if (!Array.isArray(files)) return prices;
  for (const file of files) {
    const type = normalizePrintfulPlacement((file as any)?.type);
    if (!type || type === 'mockup' || type === 'preview') continue;
    const raw = (file as any)?.additional_price;
    const amount = raw === null || raw === undefined || raw === '' ? null : Number(raw);
    prices[type] = amount === null || !Number.isFinite(amount) || amount <= 0 ? null : Math.round(amount * 100) / 100;
  }
  return prices;
}
